import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

interface StatusBadgeDropdownProps {
  currentStatus: 'PENDING' | 'CONFIRMED' | 'CANCELLED' | 'COMPLETED';
  onStatusChange: (newStatus: string) => void;
  disabled?: boolean;
}

const StatusBadgeDropdown: React.FC<StatusBadgeDropdownProps> = ({
  currentStatus,
  onStatusChange,
  disabled = false,
}) => {
  const { t } = useTranslation('translation');
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const statusConfig: Record<string, { labelKey: string; className: string; bgClassName: string }> = {
    PENDING: {
      labelKey: 'bookingStatus.PENDING',
      className: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-400',
      bgClassName: 'hover:bg-yellow-200 dark:hover:bg-yellow-800',
    },
    CONFIRMED: {
      labelKey: 'bookingStatus.CONFIRMED',
      className: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-400',
      bgClassName: 'hover:bg-blue-200 dark:hover:bg-blue-800',
    },
    COMPLETED: {
      labelKey: 'bookingStatus.COMPLETED',
      className: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-400',
      bgClassName: 'hover:bg-green-200 dark:hover:bg-green-800',
    },
    CANCELLED: {
      labelKey: 'bookingStatus.CANCELLED',
      className: 'bg-gray-100 text-gray-800 dark:bg-slate-700 dark:text-gray-300',
      bgClassName: 'hover:bg-gray-200 dark:hover:bg-slate-600',
    },
  };

  // 顯示所有狀態選項
  const allStatuses = ['PENDING', 'CONFIRMED', 'COMPLETED', 'CANCELLED'];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleStatusClick = (status: string) => {
    onStatusChange(status);
    setIsOpen(false);
  };

  const currentConfig = statusConfig[currentStatus];

  return (
    <div className="relative inline-block" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`px-1.5 py-0.5 rounded text-xs font-medium transition-colors ${currentConfig.className} ${
          disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer ' + currentConfig.bgClassName
        }`}
        title={disabled ? '' : t('common.clickToChangeStatus')}
      >
        {t(currentConfig.labelKey)}
        {!disabled && (
          <span className="ml-1 inline-block">
            <svg className="w-3 h-3 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </span>
        )}
      </button>

      {isOpen && !disabled && (
        <div className="absolute top-full left-0 mt-1 w-16 rounded-md shadow-lg z-[9999] overflow-hidden border border-gray-200 dark:border-slate-700">
          {allStatuses.map((status, index) => {
            const config = statusConfig[status];
            const isCurrent = status === currentStatus;
            return (
              <button
                key={status}
                type="button"
                onClick={() => handleStatusClick(status)}
                className={`w-full text-center px-1.5 py-0.5 text-xs font-medium transition-colors block ${config.className} ${config.bgClassName} ${
                  index !== allStatuses.length - 1 ? 'border-b border-gray-200 dark:border-slate-600' : ''
                } ${isCurrent ? 'ring-2 ring-inset ring-gray-900 dark:ring-white' : ''}`}
              >
                {t(config.labelKey)}
                {isCurrent && <span className="ml-1">✓</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default StatusBadgeDropdown;
